import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from 'config/axiosConfig';
import { Team } from 'util/constants';
import TeamProfilePlate from './TeamProfilePlate';
import LoadingSkeleton from 'ui/LoadingSkeleton';
import NotFoundPage from 'components/ResponsePages/404NotFoundPage';
import { AxiosError } from 'axios';
import ServerErrorPage from 'components/ResponsePages/500ServerErrorPage';
import AddButton from 'ui/AddButton';
import { useUser } from 'util/UserContext';
import CreateTeamModal from './CreateTeamModal';
import EmptyState from 'ui/EmptyState';
import { apiRoutes } from "util/apiRoutes";

interface Props {
    isLoading: (isLoading: boolean) => void;
    onlyMemberTeams?: boolean;
}

const TeamsList: React.FC<Props> = ({ isLoading, onlyMemberTeams = false }) => {
    const { currentUser, setShowAuthModal, awaitingUser } = useUser();
    const [teams, setTeams] = useState<Team[]>([]);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [openModal, setOpenModal] = useState(false);
    const [isDetailPanelOpen, setIsDetailPanelOpen] = useState(true);

    const fetchTeams = useCallback(async (page: number) => {
        if (onlyMemberTeams) {
            return;
        }
        try {
            setLoading(true);
            const response = await api.get(apiRoutes.team.root, {
                params: {
                    limit: 10,
                    offset: (page - 1) * 10
                },
            });

            if (response.data.length < 10) {
                setHasMore(false);
            }

            setTeams((prevTeams) => {
                const newTeams = response.data.filter(
                    (newTeam: Team) => !prevTeams.some((team) => team.id === newTeam.id)
                );
                return [...prevTeams, ...newTeams];
            });
            setLoading(false);
            isLoading(false);
        } catch (error) {
            if (error instanceof AxiosError && error.response?.status === 404) {
                setError('404: Failed to fetch Parteas');
            } else {
                setError('Failed to fetch Parteas');
            }
            setLoading(false);
            isLoading(false);
        }
    }, [isLoading, onlyMemberTeams]);

    const handlePanelOpenChange = (open: boolean) => {
        setIsDetailPanelOpen(open);
    };

    const fetchMemberTeams = useCallback(async () => {
        try {
            setLoading(true);
            const response = await api.get(apiRoutes.team.member);
            setTeams(response.data);
            setLoading(false);
        } catch (error) {
            setError('Failed to fetch member teams');
            setLoading(false);
        }
    }, []);
                    
    useEffect(() => {
        if (awaitingUser) {
            return;
        }
        setTeams([]);
        if (onlyMemberTeams) {
            fetchMemberTeams();
        } else {
            setPage(1);
            setHasMore(true);
            fetchTeams(1);
        }
    }, [fetchTeams, fetchMemberTeams, awaitingUser, onlyMemberTeams]);

    useEffect(() => {
        if (page > 1) {
            fetchTeams(page);
        }
    }, [page, fetchTeams]);

    const handleAddTeamClick = () => {
        if (!currentUser) {
            setShowAuthModal(true);
        } else {
            setOpenModal(true);
        }
    };

    const handleTeamCreated = () => {
        // Reset the teams list and refetch from the beginning
        setTeams([]);
        if (onlyMemberTeams) {
            fetchMemberTeams();
        } else {
            setPage(1);
            setHasMore(true);
            fetchTeams(1);
        }
    };

    const observer = useRef<IntersectionObserver | null>(null);
    const lastTeamElementRef = useCallback((node: HTMLDivElement | null) => {
        if (loading) return;
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) {
                setPage(prevPage => prevPage + 1);
            }
        }, {
            root: null,
            rootMargin: '0px',
            threshold: 0.1
        });
        if (node) observer.current.observe(node);
    }, [loading, hasMore]);

    if (loading && page === 1) {
        return (
            <LoadingSkeleton count={5} />
        );
    }

    if (error) {
        return error.match(/404/) ? 
            <NotFoundPage displayStyle={`page`} errorMessage={`Sorry, we couldn't find any Parteas.`} /> : 
            <ServerErrorPage />;
    }

    return (
        <div className="dark:bg-eoe-dark-bg overflow-x-hidden">
            <div className="flex flex-col space-y-1 mb-1">
                <div className="flex mt-2 mx-2">
                    <AddButton
                        onClick={handleAddTeamClick}
                        tooltipTitle="Add Partea"
                        buttonText="Partea"
                        className="add-button w-full"
                    />
                </div>
            </div>
            <div className="entity-divider flex flex-col">
                {teams.length === 0 && !loading ? (
                    <EmptyState 
                        title={onlyMemberTeams ? "You haven't joined any parteas yet" : "No parteas have been formed yet"}
                        subtitle={onlyMemberTeams ? "Join a partea to see it here" : "Be the first to create a partea!"}
                        className="m-4"
                    />
                ) : (
                    teams.map((team, index) => (
                        <div
                            key={team.id}
                            ref={index === teams.length - 1 ? lastTeamElementRef : null}
                        >
                            <TeamProfilePlate 
                                team={team as any} 
                                isDetailPanelOpen={isDetailPanelOpen}
                                onOpenPanelChange={handlePanelOpenChange}
                            />
                        </div>
                    ))
                )}
            </div>
            {openModal && (
                <CreateTeamModal
                    onClose={() => setOpenModal(false)}
                    onTeamCreated={handleTeamCreated}
                />
            )}
        </div>
    );
};

export default TeamsList; 